import React, { createContext, useContext, useReducer, useEffect } from "react";
import { createClient, User, Session } from "@supabase/supabase-js";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";

export const SUPABASE_URL_BASE = "croxnreacsjbvlgkzryl";
export const supabase = createClient(import.meta.env.VITE_SB_URL!, import.meta.env.VITE_SB_PUB!);

interface AuthState {
    user: User | null;
    role: string | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    needsConfirmation?: boolean; // new flag
}

type AuthAction =
    | { type: "LOGIN_START" }
    | { type: "LOGIN_SUCCESS"; payload: { user: User; token: string } }
    | { type: "LOGIN_FAILURE" }
    | { type: "LOGOUT" }
    | { type: "UPDATE_USER"; payload: User }
    | { type: "REGISTER_SUCCESS"; payload: { email: string } }; // new action

const initialState: AuthState = {
    user: null,
    role: null,
    isLoading: true,
    isAuthenticated: false,
    needsConfirmation: false,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
    switch (action.type) {
        case "LOGIN_START":
            return { ...state, isLoading: true };
        case "LOGIN_SUCCESS":
            return {
                ...state,
                user: action.payload.user,
                role: decodeUserRole(action.payload.token),
                isLoading: false,
                isAuthenticated: true,
                needsConfirmation: false,
            };
        case "LOGIN_FAILURE":
            return {
                ...state,
                user: null,
                role: null,
                isLoading: false,
                isAuthenticated: false,
            };
        case "LOGOUT":
            return {
                ...state,
                user: null,
                role: null,
                isLoading: false,
                isAuthenticated: false,
            };
        case "UPDATE_USER":
            return {
                ...state,
                user: action.payload,
            };
        case "REGISTER_SUCCESS":
            return {
                ...state,
                user: { email: action.payload.email } as User, // store email only
                isLoading: false,
                isAuthenticated: false,
                needsConfirmation: true,
            };
        default:
            return state;
    }
}

// decode user_role from Supabase JWT
function decodeUserRole(token: string): string | null {
    try {
        const decoded: any = jwtDecode(token);
        return decoded.user_role || null;
    } catch {
        return null;
    }
}

interface AuthContextType extends AuthState {
    tryLogin: (email: string, password: string) => Promise<void>;
    tryRegister: (email: string, password: string) => Promise<void>;
    logout: () => void;
    updateUser: (userData: Partial<User>) => Promise<void>;
    sendPasswordReset: (email: string) => Promise<void>;
    updatePassword: (password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [state, dispatch] = useReducer(authReducer, initialState);
    const navigate = useNavigate();

    useEffect(() => {
        restoreSession();
    }, []);

    async function restoreSession() {
        const { data } = await supabase.auth.getSession();
        const session = data.session;

        if (session?.user && session.access_token) {
            dispatch({
                type: "LOGIN_SUCCESS",
                payload: { user: session.user, token: session.access_token },
            });
        } else {
            dispatch({ type: "LOGIN_FAILURE" });
        }

        const { data: listener } = supabase.auth.onAuthStateChange((_event, session: Session | null) => {
            if (session?.user && session.access_token) {
                dispatch({
                    type: "LOGIN_SUCCESS",
                    payload: { user: session.user, token: session.access_token },
                });
            } else if (_event === "PASSWORD_RECOVERY") {
                navigate("/reset-password");
            } else {
                dispatch({ type: "LOGOUT" });
            }
        });

        return () => {
            listener?.subscription.unsubscribe();
        };
    }

    const tryLogin = async (email: string, password: string) => {
        dispatch({ type: "LOGIN_START" });
        const response = await supabase.auth.signInWithPassword({ email, password });
        if (!response.data.session?.user || !response.data.session.access_token || response.error) {
            dispatch({ type: "LOGIN_FAILURE" });
            throw response.error;
        }
        dispatch({
            type: "LOGIN_SUCCESS",
            payload: { user: response.data.session.user, token: response.data.session.access_token },
        });
    };

    const tryRegister = async (email: string, password: string) => {
        dispatch({ type: "LOGIN_START" });
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error || !data.user) {
            dispatch({ type: "LOGIN_FAILURE" });
            throw error;
        }
        dispatch({ type: "REGISTER_SUCCESS", payload: { email: data.user.email! } });
    };

    const logout = async () => {
        await supabase.auth.signOut();
        dispatch({ type: "LOGOUT" });
    };

    const updateUser = async (userData: Partial<User>) => {
        const { data, error } = await supabase.auth.updateUser(userData);
        if (error || !data.user) throw error;
        dispatch({ type: "UPDATE_USER", payload: data.user });
    };

    async function sendPasswordReset(email: string) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `https://daisykirbukas.ee/reset-password`,
        });
        if (error) throw error;
    }

    async function updatePassword(password: string) {
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
    }

    const value: AuthContextType = {
        ...state,
        tryLogin,
        tryRegister,
        logout,
        updateUser,
        sendPasswordReset,
        updatePassword,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within an AuthProvider");
    return context;
}
